import React, { useEffect, useState } from 'react';
import { ChevronDown, Info, AlertTriangle } from 'lucide-react';
import TarihGirisi from './TarihGirisi';

const durduranSebepTipleri = ['HAGB Kararı Verilmesi', 'Soruşturma İzni İstenilmesi', 'Bekletici Mesele', 'Diğer'];

// Bazı sebep tiplerinde tarih alanları, bağlama özgü daha anlamlı etiketlerle gösterilir.
const ALAN_ETIKETLERI = {
  'HAGB Kararı Verilmesi': {
    baslangic: 'HAGB kesinleşme tarihi',
    bitis: '2. Suç tarihi',
    gecersiz: '2. Suç tarihi, HAGB kesinleşme tarihinden önce olamaz.',
  },
  'Soruşturma İzni İstenilmesi': {
    baslangic: 'Soruşturma izni istenildiği tarih',
    bitis: 'Soruşturma izni verildiği tarih',
    gecersiz: 'İzin verildiği tarih, izin istenildiği tarihten önce olamaz.',
  },
};

const bosSebep = () => ({ tip: '', baslangic: new Date(), bitis: new Date(), aciklama: '' });

function DurduranSebepModal({ acik, kapat, kaydet, mevcut, cocuk }) {
  const [sebep, setSebep] = useState(bosSebep);
  const duzenleme = Boolean(mevcut);

  useEffect(() => {
    if (acik) {
      setSebep(
        mevcut
          ? { ...mevcut, baslangic: new Date(mevcut.baslangic), bitis: new Date(mevcut.bitis) }
          : bosSebep()
      );
    }
  }, [acik, mevcut]);

  if (!acik) return null;

  const isHagb = sebep.tip === 'HAGB Kararı Verilmesi';
  const etiket = ALAN_ETIKETLERI[sebep.tip] || {};
  const tarihGecersiz =
    sebep.baslangic && sebep.bitis && new Date(sebep.bitis) < new Date(sebep.baslangic);
  const kaydedilebilir = Boolean(sebep.tip && sebep.baslangic && sebep.bitis && !tarihGecersiz);

  // HAGB denetim süresi: yetişkinde en çok 5 yıl (CMK 231), suça sürüklenen çocukta 3 yıl (5395 s. ÇKK).
  // kesinleşme → 2. suç arası bu süreyi aşarsa uyar (engellemez).
  const denetimYil = cocuk ? 3 : 5;
  const hagbDenetimAsimi = (() => {
    if (!isHagb || tarihGecersiz || !sebep.baslangic || !sebep.bitis) return false;
    const b = new Date(sebep.baslangic);
    const sinir = new Date(b.getFullYear() + denetimYil, b.getMonth(), b.getDate());
    return new Date(sebep.bitis) > sinir;
  })();

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div className="w-full max-w-md rounded-xl border border-slate-200 bg-white shadow-2xl">
        <div className="border-b border-slate-100 px-5 py-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-amber-600">
            TCK 67/1 · Durma
          </p>
          <h2 className="text-lg font-semibold text-slate-800">
            {duzenleme ? 'Durduran sebebi düzenle' : 'Durduran sebep ekle'}
          </h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Süreyi durdurur; bu aralık kadar hem olağan hem üst sınır tarihi ötelenir.
          </p>
        </div>

        <div className="space-y-4 px-5 py-5">
          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Sebep</label>
            <div className="relative">
              <select
                value={sebep.tip}
                onChange={(e) => setSebep({ ...sebep, tip: e.target.value })}
                className="w-full appearance-none rounded-lg border border-slate-300 bg-white px-3 py-2.5 pr-9 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
              >
                <option value="">Sebep seçiniz…</option>
                {durduranSebepTipleri.map((tip) => (
                  <option key={tip} value={tip}>
                    {tip}
                  </option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <TarihGirisi
              label={etiket.baslangic || 'Başlangıç'}
              value={sebep.baslangic}
              onChange={(date) => setSebep({ ...sebep, baslangic: date })}
            />
            <TarihGirisi
              label={etiket.bitis || 'Bitiş'}
              value={sebep.bitis}
              onChange={(date) => setSebep({ ...sebep, bitis: date })}
            />
          </div>
          {tarihGecersiz ? (
            <p className="text-xs font-medium text-rose-600">
              {etiket.gecersiz || 'Bitiş tarihi başlangıçtan önce olamaz.'}
            </p>
          ) : null}

          {hagbDenetimAsimi ? (
            <div className="flex gap-2.5 rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />
              <p className="text-xs leading-relaxed text-rose-700">
                HAGB kesinleşme ile 2. suç tarihi arasında <span className="font-medium">{denetimYil} yıldan fazla</span>{' '}
                süre var.{' '}
                {cocuk
                  ? 'Suça sürüklenen çocuklarda denetim süresi en çok 3 yıldır (5395 s. ÇKK).'
                  : 'HAGB denetim süresi en çok 5 yıldır (CMK 231).'}{' '}
                2. suç bu süre içinde işlenmiş olmalıdır. Tarihleri kontrol edin.
              </p>
            </div>
          ) : null}

          {isHagb ? (
            <div className="flex gap-2.5 rounded-lg border border-amber-200 bg-amber-50/70 px-3 py-2.5">
              <Info className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs leading-relaxed text-amber-800">
                Durma, HAGB kesinleşmesinden denetim süresinde işlenen 2. suça kadar işler. 2. suç{' '}
                <span className="font-medium">kasıtlı ve kesinleşmiş</span> olmalıdır.
              </p>
            </div>
          ) : null}

          <div>
            <label className="mb-1.5 block text-sm font-medium text-slate-700">Açıklama (opsiyonel)</label>
            <input
              type="text"
              placeholder="Örn. izin makamı / dosya numarası"
              value={sebep.aciklama}
              onChange={(e) => setSebep({ ...sebep, aciklama: e.target.value })}
              className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm text-slate-900 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30"
            />
          </div>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-4">
          <button
            type="button"
            onClick={kapat}
            className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            İptal
          </button>
          <button
            type="button"
            disabled={!kaydedilebilir}
            onClick={() => kaydet(sebep)}
            className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {duzenleme ? 'Kaydet' : 'Ekle'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DurduranSebepModal;
